import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Paper,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import InfiniteScroll from "react-infinite-scroll-component";
import { backgroundColor, toMonthDayYearFormat, windDirectionChange, SFColors } from "src/store/common";
import { humidityColors, precip, solarRadiationColors, temperatureColors, vpdColors, windGustColors } from "src/constants";
import { useAddAlert } from "src/store/actions";
import { useStoreContext } from "src/store/context";
import moment from "moment";
import { getWeatherMinMax } from "src/apis/getWeatherCalls";
import arrow from 'src/assets/images/arrow.png';

type WxDailyProps = {
    station: any,
    startDate: any,
    endDate: any,
}


export const WxDaily: React.FC<WxDailyProps> = ({ station, startDate,endDate}) =>{
  const [ , dispatch] = useStoreContext();
  const [wxDailyData, setWxDailyData] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const addAlert = useAddAlert(dispatch);
  const [wxDailyFailCount, setWxDailyFailCount] = useState<number>(0);

  const wxDailyCallback = useCallback((count:number) => {
    (async () => {
      try {
        const request = {
          startDate: moment(startDate).format('YYYY-MM-DD'),
          endDate: moment(endDate).format('YYYY-MM-DD'),
          stationIds: station.station_id.toString(),
          zoomLevel: 5,
          page: 0,
          perPage: 25,
          sortBy: 'summary_date',
          sortOrder: 'desc'
        }

        const response: any = await getWeatherMinMax(request, addAlert);
        if(response.data ) {
          setWxDailyFailCount(0)
          setWxDailyData(response.data);
          if((response._metadata.page ===0 && response._metadata.page_count ===1)||(response._metadata.page=== response._metadata.page_count)){
            setHasMore(false)
          }
          else{
            setHasMore(true)
          }
        }
      } catch (error) {
        setWxDailyFailCount(count+1)
        console.log('error', error)
      }
    })();
  },[startDate, endDate, station, addAlert]);

  const fetchMoreData = useCallback(() =>{
    (async () => {
      try {
        const request = {
          startDate: moment(startDate).format('YYYY-MM-DD'),
          endDate: moment(endDate).format('YYYY-MM-DD'),
          stationIds: station.station_id.toString(),
          zoomLevel: 5,
          page: page,
          perPage: 25,
          sortBy: 'summary_date',
          sortOrder: 'desc'
        }

        const response: any = await getWeatherMinMax(request, addAlert);
        
        setWxDailyData((prevData) => [...prevData, ...response.data]);
        if((response._metadata.page ===0 && response._metadata.page_count ===1)||(response._metadata.page=== response._metadata.page_count)){
          setHasMore(false)
        }
        else{
          setHasMore(true)
        }
        setPage(page + 1);
      } catch (error) {
        setHasMore(false);
        console.log('error', error)
      }
    })();
  },[startDate, endDate, station, page, addAlert]);

  /**
   * Initialize the dataset when modifying the parameters
   */
  useEffect(() => {
    if(station){
      setPage(1);
      setHasMore(true);
      wxDailyCallback(wxDailyFailCount);
    }
    if(wxDailyFailCount>0 && wxDailyFailCount<4 && station){
      wxDailyCallback(wxDailyFailCount);
    }
  },[startDate, endDate, station, wxDailyCallback, wxDailyFailCount]);
    
  return (
    <Box>
      {/* Table */}
      <Paper sx ={{width: '99%'}}>
        <TableContainer className="tableWxDaily smooth-scrollbar" id="scrollableTable">
          <TableHead className="tableHead"
            style={{
              position: "sticky",
              top: 0,
              display: 'flex',
              zIndex: 110,
              backgroundColor: "#A1A1A1",
            }}
          >
            <TableRow>
              <TableCell >Date(LST)</TableCell>
              <TableCell >Max Temp(°F)</TableCell>
              <TableCell >Min Temp(°F)</TableCell>
              <TableCell  >Max RH(%)</TableCell>
              <TableCell  >Min RH(%)</TableCell>
              <TableCell  >Max Wind(mph)</TableCell>
              <TableCell  >Max Wind Gust Dir(time/°)</TableCell>
              <TableCell  >Max Wind Gust(mph)</TableCell>
              <TableCell  >Precip Total(in)</TableCell>
              <TableCell  >Max SR(W/m2)</TableCell>
              <TableCell  >Max VPD(Pa)</TableCell>
              <TableCell  >Min VPD(Pa)</TableCell>
              <TableCell  >SF</TableCell>
            </TableRow>
          </TableHead>
          {station?.weather_daily_visible === false &&
            <TableBody>
              <div style={{ padding: '15px' }}>
                No <span style={{ fontWeight: '700' }}>Daily</span> Data Available for this <span style={{ fontWeight: '700' }}>Restricted Station</span>
              </div>
            </TableBody>
          }
          {
            !hasMore && wxDailyData?.length===0? (
              station?.weather_daily_visible &&
              <TableBody>
                  <div style={{padding: '15px'}}>
                    No Data Available for this Station for the Selected Date Range
                  </div>
              </TableBody>
            ):(
              station?.weather_daily_visible &&
              <InfiniteScroll
              dataLength={wxDailyData?.length}
              next={fetchMoreData}
              hasMore={hasMore}
              loader={<p>Loading...</p>}
              scrollableTarget="scrollableTable"
            >
              <TableBody>
                  {station?.weather_daily_visible && wxDailyData?.map((row, index) => (
                    <TableRow
                      className="row"
                      key={`${row.summary_date} ${index}`}
                      style={{
                        width: '100%',
                        border: row.observation_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                      }}
                    >
                      <TableCell
                        style={{
                          backgroundColor: "rgb(175 158 158 / 87%)",
                          position: "sticky",
                          left: 0,
                          zIndex: 100,
                        }}
                      >
                        {toMonthDayYearFormat(row.summary_date)}
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: backgroundColor(row.temperature_max, temperatureColors).bg,
                          color: backgroundColor(row.temperature_max, temperatureColors).text,
                          border: row.observation_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                          
                        }}
                      >
                        {(row.temperature_max)}
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: backgroundColor(row.temperature_min, temperatureColors).bg,
                          color: backgroundColor(row.temperature_min, temperatureColors).text,
                          border: row.observation_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                          borderColor: 'black',
                          
                        }}
                      >
                        {(row.temperature_min)}
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: backgroundColor(row.relative_humidity_max, humidityColors).bg,
                          color: backgroundColor(row.relative_humidity_max, humidityColors).text,
                          border: row.observation_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                          borderColor: '#222222',
                        }}
                      >
                        {(row.relative_humidity_max)}
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: backgroundColor(row.relative_humidity_min, humidityColors).bg,
                          color: backgroundColor(row.relative_humidity_min, humidityColors).text,
                          border: row.observation_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                          borderColor: '#222222',
  
                        }}
                      >
                        {(row.relative_humidity_min)}
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: backgroundColor(row.wind_speed_max, windGustColors).bg,
                          color: backgroundColor(row.wind_speed_max, windGustColors).text,
                          border: row.observation_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                          borderColor: '#222222',
  
                        }}
                      >
                        {(row.wind_speed_max)}
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: 'white',
                          color: 'black',
                          border: row.observation_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                          borderColor: '#222222',
  
                        }}
                      >
                        {`${row.peak_wind_gust_time}:00 /${row.peak_wind_gust_direction} `}<img src={arrow} style={{ width: '15px', transform: 'rotate(' + windDirectionChange(row.peak_wind_gust_direction+270) + 'deg)' }} alt={`${row.peak_wind_gust_direction} at ${row.summary_date}`} />
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: backgroundColor(row.peak_gust_speed_max, windGustColors).bg,
                          color: backgroundColor(row.peak_gust_speed_max, windGustColors).text,
                          border: row.observation_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                          borderColor: '#222222',
    
                      }}
                      >
                        {(row.peak_gust_speed_max)}
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: backgroundColor(row.daily_precipitation_total, precip).bg,
                          color: backgroundColor(row.daily_precipitation_total, precip).text,
                          border: row.observation_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                          borderColor: '#222222',
    
                      }}
                      >
                        {(row.daily_precipitation_total)}
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: backgroundColor(row.solar_radiation_max, solarRadiationColors).bg,
                          color: backgroundColor(row.solar_radiation_max, solarRadiationColors).text,
                          border: row.observation_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                          borderColor: '#222222',
    
                      }}
                      >
                        {(row.solar_radiation_max)}
                      </TableCell>

                      <TableCell
                        style={{
                          backgroundColor: backgroundColor(row.vapor_press_def_max, vpdColors).bg,
                          color: backgroundColor(row.vapor_press_def_max, vpdColors).text,
                          border: row.observation_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                          borderColor: '#222222',
    
                      }}
                      >
                        {(row.vapor_press_def_max)}
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: backgroundColor(row.vapor_press_def_min, vpdColors).bg,
                          color: backgroundColor(row.vapor_press_def_min, vpdColors).text,
                          border: row.observation_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                          borderColor: '#222222',
    
                      }}
                      >
                        {(row.vapor_press_def_min)}
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: SFColors(row.snow_flag).bg,
                          color: SFColors(row.snow_flag).text,
                          border: row.observation_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                          borderColor: '#222222',
    
                      }}
                      >
                        {(row.snow_flag)} 
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </InfiniteScroll>

            )
          }
          </TableContainer>
        </Paper>    
    </Box>
  );
}
